import { Box, Flex, Text } from "@powerledger/ui-component-lib";
import currency from "currency.js";
import { useTranslation } from "react-i18next";

import { getTotalMarketValue, getTotalValue, getTransactionFee } from "@/app/lib/order-helpers";
import { OrderPosition, TradePosition } from "@/app/types/generated/graphql";

type OrderSummaryProps = Readonly<{
  currencySymbol: string;
  title: string;
  unitPrice: number;
  volume: number;
  orderPosition: OrderPosition | TradePosition;
  fee: number;
}>;

export default function OrderSummary({
  currencySymbol,
  title,
  unitPrice,
  volume,
  orderPosition,
  fee,
}: OrderSummaryProps) {
  const { t } = useTranslation();

  const offerType = orderPosition === OrderPosition.Ask ? "Offer" : "Bid";
  const debitedCredited = orderPosition === OrderPosition.Ask ? "credited" : "debited";

  return (
    <>
      <Box sx={{ my: 3, fontFamily: "MintGroteskV08", fontWeight: "bold", fontSize: 3 }}>{t(title)}</Box>
      <Box sx={{ fontSize: 1 }} data-testid={"order-summary"}>
        <Flex sx={{ justifyContent: "space-between", mt: 1 }} data-testid={"product-quantity"}>
          <Text>{t("Product Quantity")}</Text>
          {/* format 1000 -> 1,000 */}
          <Text>{Number(volume).toLocaleString("en-US")}</Text>
        </Flex>
        <Flex sx={{ justifyContent: "space-between", mt: 1 }} data-testid={`${offerType.toLowerCase()}-price-per-unit`}>
          <Text>{t("{{offerType}} Price per Unit", { offerType })}</Text>
          <Text>{currencySymbol + currency(unitPrice).value.toLocaleString("en-US")}</Text>
        </Flex>
        <Flex sx={{ justifyContent: "space-between", mt: 1 }} data-testid={"product-value"}>
          <Text>{t("Trade Value")}</Text>
          <Text>{currencySymbol + getTotalMarketValue(unitPrice, volume).value.toLocaleString("en-US")}</Text>
        </Flex>
        <Flex
          sx={{
            mt: 1,
            justifyContent: "space-between",
            pb: 3,
            borderBottomWidth: 1,
            borderBottomStyle: "solid",
            borderBottomColor: "textDarker",
          }}
          data-testid={"transaction-fee"}
        >
          <Text>
            {t("Transaction Fee - {{value}}% (payable to Market Operator)", {
              value: fee * 100,
            })}
          </Text>
          <Text>
            {unitPrice & volume && offerType === "Bid" ? "+" : ""}
            {unitPrice & volume && offerType === "Offer" ? "-" : ""}
            {currencySymbol + getTransactionFee(unitPrice, volume, fee).value.toLocaleString("en-US")}
          </Text>
        </Flex>
        <Flex
          sx={{
            justifyContent: "space-between",
            pt: 3,
          }}
          data-testid={"total-order-value"}
        >
          <Text>{t("Net Order Amount {{debitedCredited}}", { debitedCredited })}</Text>
          <Text>
            {currencySymbol + getTotalValue(unitPrice, volume, orderPosition, fee).value.toLocaleString("en-US")}
          </Text>
        </Flex>
      </Box>
    </>
  );
}
